import posthog, { type PostHogConfig } from 'posthog-js';

import { SENSITIVE_QUERY_PARAMS, sanitizeCaptureResult } from '@/lib/analyticsPrivacy';
import type { OAuthIntent, OAuthProvider } from '@/lib/oauth';

// Browser-side PostHog helpers for the SDK bootstrap (`instrumentation-client.ts`) and
// identity changes (`@/context/UserInfoContext`).

/**
 * Stamps the deploy environment onto every subsequent browser event.
 *
 * Must match the server's `DeployEnv` strings, which the shared project separates traffic
 * by. Absent locally, where analytics is off.
 */
export function registerDeployProperties(): void {
  const environment = process.env.NEXT_PUBLIC_POSTHOG_ENVIRONMENT;
  if (environment) {
    posthog.register({ environment });
  }
}

/**
 * Returns this browser to an anonymous `distinct_id`, keeping deploy properties.
 *
 * `posthog.reset()` also clears super-properties, and the bootstrap only runs on a full
 * page load, so without this later events would lose `environment`.
 */
export function resetAnalyticsIdentity(): void {
  posthog.reset();
  registerDeployProperties();
}

/**
 * Browser analytics events, by name.
 *
 * The browser records intent and the server (`rust/base/src/analytics/event.rs`)
 * records outcomes, so names never overlap. Names are stable once shipped.
 */
export const BROWSER_EVENT = {
  /** The user chose an OAuth provider and is about to be sent to it. */
  OAUTH_STARTED: 'oauth_started',
  /**
   * The user submitted the password reset request form.
   *
   * Recorded on submission: the response is the same for unknown addresses, so
   * only the server's `password_reset_requested` knows if a reset was sent.
   */
  PASSWORD_RESET_REQUEST_SUBMITTED: 'password_reset_request_submitted',
} as const;

/** Name of one browser analytics event. */
type BrowserEventName = (typeof BROWSER_EVENT)[keyof typeof BROWSER_EVENT];

/**
 * Properties each browser event carries.
 *
 * Bounded identifiers only: no personal data, credentials, or user-typed text.
 */
type BrowserEventProperties = {
  [BROWSER_EVENT.OAUTH_STARTED]: { intent: OAuthIntent; provider: OAuthProvider };
  [BROWSER_EVENT.PASSWORD_RESET_REQUEST_SUBMITTED]: Record<string, never>;
};

/**
 * Capture one browser analytics event, type-checked against `BrowserEventProperties`.
 *
 * A no-op where the SDK is not initialized, as in local development.
 */
export function captureBrowserEvent<E extends BrowserEventName>(
  event: E,
  properties: BrowserEventProperties[E]
): void {
  posthog.capture(event, properties);
}

/**
 * Options the browser SDK is initialized with, sending to `apiHost`.
 *
 * Built here so tests drive the real SDK with the production configuration.
 */
// `disable_compression` is a valid runtime SDK option that the current typings omit, so
// the return type widens `PostHogConfig` to admit it. See its comment below.
export function posthogInitOptions(
  apiHost: string
): Partial<PostHogConfig> & { disable_compression?: boolean } {
  return {
    // `apiHost` is our same-origin proxy (`/ingest`, see `src/proxy.ts`), which ad
    // blockers don't block. `ui_host` points at the real PostHog UI for toolbar links.
    api_host: apiHost,
    ui_host: 'https://us.posthog.com',
    // Send events uncompressed (`data=<base64>`), not gzip. The `/ingest` proxy corrupts
    // binary request bodies, so gzipped batches reach PostHog mangled and are rejected;
    // the ASCII base64 form survives. Re-enable once the proxy forwards binary bodies intact.
    disable_compression: true,
    defaults: '2026-01-30',
    debug: process.env.NODE_ENV === 'development',
    // Collection policy, pinned rather than left to SDK defaults: clicks without
    // element text or attributes (both can contain names, as in `UserLink`'s
    // accessibility label), pageviews and pageleaves on client-side navigation, and
    // uncaught exceptions. Link targets are still captured, and sanitized by `before_send`.
    autocapture: true,
    mask_all_text: true,
    mask_all_element_attributes: true,
    capture_pageview: 'history_change',
    capture_pageleave: true,
    capture_exceptions: true,
    // Everything else is off here, overriding project settings. Heatmaps key their data
    // by page URL, which `before_send` doesn't sanitize, so enabling them needs that
    // extended first. Dead clicks and web vitals are sanitized and safe to enable.
    capture_heatmaps: false,
    capture_dead_clicks: false,
    capture_performance: false,
    // URL privacy; see `@/lib/analyticsPrivacy`.
    disable_capture_url_hashes: true,
    mask_personal_data_properties: true,
    custom_personal_data_properties: SENSITIVE_QUERY_PARAMS,
    before_send: sanitizeCaptureResult,
    // Recordings bypass `before_send` and carry full page URLs. This overrides the
    // project setting, keeping replay off until the capture policy covers it.
    disable_session_recording: true,
  };
}
